import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Row,
  Col,
  Switch,
  Space,
  DatePicker,
  Divider,
  message,
} from "antd";
import { HomeOutlined, PlusOutlined, DeleteOutlined, UploadOutlined, LoadingOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import {
  ClientApartment,
  ApartmentType,
  ApartmentCurrency,
  PricingRule,
  PricingRuleType,
  BookingWindowMode,
  ApartmentExtra,
  WeekdayRule,
  LastMinuteRule,
  EarlyBookingRule,
  NightCountRule,
  createClientApartment,
  updateClientApartment,
} from "../../../utils/api/apartmentRentalsAPI";
import { uploadFileToR2 } from "../../../utils/uploadToR2";
import { PRICING_UNITS, PRICING_UNIT_LABELS } from "../../../utils/apartmentCalculator";

const { TextArea } = Input;
const { Option } = Select;

// Cover gradient presets
const GRADIENT_PRESETS = [
  {
    label: "Ocean Blue",
    value: "linear-gradient(135deg, oklch(0.55 0.15 220) 0%, oklch(0.7 0.12 180) 100%)",
    preview: "#3b82f6",
  },
  {
    label: "Warm Sunset",
    value: "linear-gradient(135deg, oklch(0.55 0.16 30) 0%, oklch(0.65 0.14 60) 100%)",
    preview: "#f97316",
  },
  {
    label: "Purple Night",
    value: "linear-gradient(135deg, oklch(0.5 0.16 280) 0%, oklch(0.6 0.18 320) 100%)",
    preview: "#8b5cf6",
  },
  {
    label: "Forest Green",
    value: "linear-gradient(135deg, oklch(0.55 0.14 158) 0%, oklch(0.65 0.12 130) 100%)",
    preview: "#16a34a",
  },
  {
    label: "Rose Gold",
    value: "linear-gradient(135deg, oklch(0.65 0.16 20) 0%, oklch(0.7 0.14 40) 100%)",
    preview: "#f43f5e",
  },
  {
    label: "Midnight",
    value: "linear-gradient(135deg, oklch(0.3 0.06 260) 0%, oklch(0.45 0.1 280) 100%)",
    preview: "#1e1b4b",
  },
];

const TAG_OPTIONS = [
  "Premium", "Family", "Boutique", "Luxury", "Budget", "Studio",
  "Pet Friendly", "Sea View", "Mountain View", "City Center",
];

const AMENITY_OPTIONS = [
  "WiFi", "Parking", "Air Conditioning", "Heating", "Kitchen",
  "Washing Machine", "Balcony", "Pool", "Gym", "TV", "Dishwasher", "BBQ",
];

const CURRENCY_OPTIONS: { label: string; value: ApartmentCurrency }[] = [
  { label: "RSD – Srpski dinar", value: "RSD" },
  { label: "EUR – Euro", value: "EUR" },
  { label: "BAM – Konvertibilna marka", value: "BAM" },
];

interface LocalPricingRule {
  from: Dayjs | null;
  to: Dayjs | null;
  type: PricingRuleType;
  value: number;
  label: string;
}

const emptyRule = (): LocalPricingRule => ({
  from: null,
  to: null,
  type: "discount",
  value: 10,
  label: "",
});

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: (apt: ClientApartment) => void;
  editData?: ClientApartment | null;
  clientId: string;
}

const AddApartmentModal: React.FC<Props> = ({
  open,
  onClose,
  onSaved,
  editData,
  clientId,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedGradient, setSelectedGradient] = useState(GRADIENT_PRESETS[0].value);
  const [pricingRules, setPricingRules] = useState<LocalPricingRule[]>([]);
  const [windowMode, setWindowMode] = useState<BookingWindowMode>("unlimited");
  const [windowFixedDate, setWindowFixedDate] = useState<Dayjs | null>(null);
  const [windowRollingDays, setWindowRollingDays] = useState<number>(90);
  const [aptExtras, setAptExtras] = useState<ApartmentExtra[]>([]);
  const [weekdayRules, setWeekdayRules] = useState<WeekdayRule[]>([]);
  const [nightCountRules, setNightCountRules] = useState<NightCountRule[]>([]);
  const [lastMinuteRule, setLastMinuteRule] = useState<LastMinuteRule>({ enabled: false, daysThreshold: 3, discount: 20 });
  const [earlyBookingRule, setEarlyBookingRule] = useState<EarlyBookingRule>({ enabled: false, daysThreshold: 30, discount: 15 });
  const [images, setImages] = useState<string[]>([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragIndex = useRef<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const isEdit = !!editData;

  useEffect(() => {
    if (open) {
      if (editData) {
        form.setFieldsValue({
          name: editData.name,
          location: editData.location,
          description: editData.description,
          pricePerNight: editData.pricePerNight,
          minNights: editData.minNights ?? 1,
          capacity: editData.capacity,
          type: editData.type,
          tags: editData.tags,
          amenities: editData.amenities,
          isActive: editData.isActive,
          currency: editData.currency || "RSD",
        });
        setSelectedGradient(editData.coverGradient || GRADIENT_PRESETS[0].value);
        setImages(editData.images || []);
        setAptExtras(editData.extras || []);
        setWeekdayRules(editData.weekdayRules || []);
        setNightCountRules(editData.nightCountRules || []);
        setLastMinuteRule(editData.lastMinute || { enabled: false, daysThreshold: 3, discount: 20 });
        setEarlyBookingRule(editData.earlyBooking || { enabled: false, daysThreshold: 30, discount: 15 });
        setPricingRules(
          (editData.pricingRules || []).map((r) => ({
            from: r.from ? dayjs(r.from) : null,
            to: r.to ? dayjs(r.to) : null,
            type: r.type,
            value: r.value,
            label: r.label || "",
          }))
        );
        const bw = editData.bookingWindow;
        setWindowMode(bw?.mode || "unlimited");
        setWindowFixedDate(bw?.fixedDate ? dayjs(bw.fixedDate) : null);
        setWindowRollingDays(bw?.rollingDays || 90);
      } else {
        form.resetFields();
        form.setFieldsValue({ type: "apartment", capacity: 2, minNights: 1, isActive: true, currency: "RSD" });
        setSelectedGradient(GRADIENT_PRESETS[0].value);
        setImages([]);
        setAptExtras([]);
        setWeekdayRules([]);
        setNightCountRules([]);
        setLastMinuteRule({ enabled: false, daysThreshold: 3, discount: 20 });
        setEarlyBookingRule({ enabled: false, daysThreshold: 30, discount: 15 });
        setPricingRules([]);
        setWindowMode("unlimited");
        setWindowFixedDate(null);
        setWindowRollingDays(90);
      }
    }
  }, [open, editData, form]);

  const updateRule = (i: number, patch: Partial<LocalPricingRule>) => {
    setPricingRules((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };

  const removeRule = (i: number) => {
    setPricingRules((prev) => prev.filter((_, idx) => idx !== i));
  };

  const handleDragStart = (i: number) => { dragIndex.current = i; };
  const handleDragOver = (e: React.DragEvent, i: number) => { e.preventDefault(); setDragOverIdx(i); };
  const handleDrop = (i: number) => {
    const from = dragIndex.current;
    if (from === null || from === i) { setDragOverIdx(null); return; }
    setImages((prev) => {
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(i, 0, item);
      return next;
    });
    dragIndex.current = null;
    setDragOverIdx(null);
  };
  const handleDragEnd = () => { dragIndex.current = null; setDragOverIdx(null); };

  const slugifyName = (s: string) =>
    s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "apartment";

  const handleImageFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploadingImages(true);
    try {
      const aptName: string = editData?.name || form.getFieldValue("name") || "apartment";
      const prefix = `apartments/${slugifyName(aptName)}`;
      const uploaded: string[] = [];
      for (const file of Array.from(files)) {
        if (!file.type.startsWith("image/")) continue;
        const result = await uploadFileToR2(file, prefix, clientId);
        uploaded.push(result.url);
      }
      setImages((prev) => [...prev, ...uploaded]);
    } catch {
      message.error("Greška pri uploadu slike");
    } finally {
      setUploadingImages(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const updateExtra = (i: number, patch: Partial<ApartmentExtra>) => {
    setAptExtras((prev) => prev.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));
  };

  const removeExtra = (i: number) => {
    setAptExtras((prev) => prev.filter((_, idx) => idx !== i));
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload: Partial<ClientApartment> = {
        ...values,
        images,
        coverGradient: selectedGradient,
        extras: aptExtras.filter((e) => e.name.trim() && e.regularPrice > 0),
        weekdayRules: weekdayRules.filter((r) => r.days.length > 0 && r.value > 0),
        nightCountRules: nightCountRules.filter((r) => r.nights >= 1 && r.pricePerNight > 0),
        lastMinute: lastMinuteRule,
        earlyBooking: earlyBookingRule,
        pricingRules: pricingRules
          .filter((r) => r.from && r.to && r.value > 0)
          .map((r) => ({
            type: r.type,
            label: r.label || undefined,
            from: r.from!.format("YYYY-MM-DD"),
            to: r.to!.format("YYYY-MM-DD"),
            value: r.value,
          })) as PricingRule[],
        bookingWindow: {
          mode: windowMode,
          fixedDate: windowMode === "fixed_date" && windowFixedDate
            ? windowFixedDate.format("YYYY-MM-DD")
            : null,
          rollingDays: windowMode === "rolling" ? windowRollingDays : null,
        },
      };

      let result;
      if (isEdit && editData) {
        result = await updateClientApartment(editData._id, payload);
      } else {
        result = await createClientApartment(clientId, payload);
      }

      onSaved(result.data);
      onClose();
    } catch (err: any) {
      if (err?.response?.data?.message) {
        // Field validation from server
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <Space>
          <span
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: "#ede9fd",
              color: "#5b50d6",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 16,
            }}
          >
            <HomeOutlined />
          </span>
          <span style={{ fontSize: 16, fontWeight: 600 }}>
            {isEdit ? "Izmijeni apartman" : "Dodaj apartman"}
          </span>
        </Space>
      }
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={onClose}>Otkaži</Button>
          <Button
            type="primary"
            loading={loading}
            onClick={handleSave}
            style={{ background: "#1a1520", borderColor: "#1a1520" }}
          >
            {isEdit ? "Sačuvaj izmjene" : "Dodaj apartman"}
          </Button>
        </div>
      }
      width={660}
      destroyOnClose
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        {/* Cover gradient picker */}
        <Form.Item label="Boja naslovnice">
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {GRADIENT_PRESETS.map((g) => (
              <div
                key={g.value}
                onClick={() => setSelectedGradient(g.value)}
                title={g.label}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  background: g.value,
                  cursor: "pointer",
                  border:
                    selectedGradient === g.value
                      ? "3px solid #1a1520"
                      : "3px solid transparent",
                  boxShadow:
                    selectedGradient === g.value
                      ? "0 0 0 2px #fff, 0 0 0 4px #1a1520"
                      : "none",
                  transition: "all 0.15s",
                }}
              />
            ))}
          </div>
        </Form.Item>

        {/* ─── Photos ─────────────────────────────────────────────── */}
        <Form.Item label="Fotografije apartmana">
          {images.length > 0 && (
            <>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
                {images.map((url, i) => (
                  <div
                    key={url}
                    draggable
                    onDragStart={() => handleDragStart(i)}
                    onDragOver={(e) => handleDragOver(e, i)}
                    onDrop={() => handleDrop(i)}
                    onDragEnd={handleDragEnd}
                    style={{
                      position: "relative",
                      width: 88,
                      height: 88,
                      borderRadius: 10,
                      overflow: "hidden",
                      border: dragOverIdx === i
                        ? "2.5px dashed #5b50d6"
                        : i === 0
                        ? "2.5px solid #5b50d6"
                        : "2px solid #e8e5f0",
                      cursor: "grab",
                      opacity: dragIndex.current === i ? 0.45 : 1,
                      transition: "border-color 0.15s, opacity 0.15s",
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={url}
                      alt={`slika ${i + 1}`}
                      draggable={false}
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                    {/* Primary badge */}
                    {i === 0 && (
                      <div style={{
                        position: "absolute", bottom: 0, left: 0, right: 0,
                        background: "rgba(91,80,214,0.85)",
                        color: "#fff", fontSize: 9, fontWeight: 700,
                        textAlign: "center", padding: "3px 0", letterSpacing: "0.04em",
                        textTransform: "uppercase",
                      }}>
                        ★ Primarna
                      </div>
                    )}
                    {/* Delete button */}
                    <button
                      type="button"
                      onClick={() => setImages((prev) => prev.filter((_, idx) => idx !== i))}
                      style={{
                        position: "absolute", top: 4, right: 4, width: 20, height: 20,
                        borderRadius: "50%", border: "none", background: "rgba(0,0,0,0.6)",
                        color: "#fff", fontSize: 12, cursor: "pointer", display: "flex",
                        alignItems: "center", justifyContent: "center", padding: 0, lineHeight: 1,
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 11, color: "#9893a8", marginBottom: 10 }}>
                Prevucite slike da promijenite redosljed · Prva slika je primarna (prikazuje se na vrhu)
              </div>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={(e) => handleImageFiles(e.target.files)}
          />
          <Button
            icon={uploadingImages ? <LoadingOutlined /> : <UploadOutlined />}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingImages}
            style={{ borderColor: "#c4bfef", color: "#5b50d6" }}
          >
            {uploadingImages ? "Upload..." : "Dodaj fotografije"}
          </Button>
        </Form.Item>

        <Row gutter={12}>
          <Col span={14}>
            <Form.Item
              name="name"
              label="Naziv apartmana"
              rules={[{ required: true, message: "Unesite naziv" }]}
            >
              <Input placeholder="npr. Tara Lux Studio" />
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item name="type" label="Tip" rules={[{ required: true }]}>
              <Select>
                <Option value="studio">Studio</Option>
                <Option value="apartment">Apartman</Option>
                <Option value="loft">Loft</Option>
                <Option value="villa">Vila</Option>
                <Option value="house">Kuća</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="location"
          label="Lokacija"
          rules={[{ required: true, message: "Unesite lokaciju" }]}
        >
          <Input placeholder="npr. Tara – Mitrovac" />
        </Form.Item>

        <Row gutter={12}>
          <Col span={8}>
            <Form.Item
              name="currency"
              label="Valuta"
              rules={[{ required: true }]}
            >
              <Select options={CURRENCY_OPTIONS} />
            </Form.Item>
          </Col>
          <Col span={9}>
            <Form.Item
              name="pricePerNight"
              label="Osnovna cijena po noći"
              rules={[{ required: true, message: "Unesite cijenu" }]}
            >
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                parser={(v) => Number(v?.replace(/\./g, "")) as any}
                placeholder="12.000"
              />
            </Form.Item>
          </Col>
          <Col span={7}>
            <Form.Item
              name="capacity"
              label="Kapacitet gostiju"
              rules={[{ required: true, message: "Unesite kapacitet" }]}
            >
              <InputNumber min={1} max={20} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={8}>
            <Form.Item
              name="minNights"
              label="Minimum noći"
              tooltip="Najmanji broj noći koji gost mora rezervisati"
              rules={[{ required: true, message: "Unesite minimum" }]}
            >
              <InputNumber
                min={1}
                max={365}
                style={{ width: "100%" }}
                addonAfter="noći"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="tags" label="Tagovi">
          <Select
            mode="tags"
            placeholder="Odaberite ili upišite tag"
            options={TAG_OPTIONS.map((t) => ({ label: t, value: t }))}
          />
        </Form.Item>

        <Form.Item name="amenities" label="Sadržaj / Pogodnosti">
          <Select
            mode="multiple"
            placeholder="Odaberite sadržaj"
            options={AMENITY_OPTIONS.map((a) => ({ label: a, value: a }))}
          />
        </Form.Item>

        <Form.Item name="description" label="Opis">
          <TextArea rows={3} placeholder="Kratki opis apartmana..." />
        </Form.Item>

        <Form.Item name="isActive" label="Aktivan" valuePropName="checked">
          <Switch />
        </Form.Item>

        {/* ─── Booking Window ────────────────────────────────────────── */}
        <Divider style={{ margin: "8px 0 16px" }}>
          <span style={{ fontSize: 13, color: "#6b6880", fontWeight: 500 }}>
            Period dostupnosti za rezervaciju
          </span>
        </Divider>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: "#6b6880", marginBottom: 10 }}>
            Do kada gosti mogu rezervisati ovaj apartman?
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {/* Option: unlimited */}
            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", padding: "10px 12px", border: `1.5px solid ${windowMode === "unlimited" ? "#5b50d6" : "#e8e5f0"}`, borderRadius: 8, background: windowMode === "unlimited" ? "#ede9fd" : "#fff" }}>
              <input type="radio" name="bwMode" checked={windowMode === "unlimited"} onChange={() => setWindowMode("unlimited")} style={{ accentColor: "#5b50d6" }} />
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, color: "#1a1520" }}>♾ Trajno dostupno</div>
                <div style={{ fontSize: 11, color: "#6b6880" }}>Gosti mogu rezervisati bilo koji budući datum</div>
              </div>
            </label>

            {/* Option: rolling */}
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", padding: "10px 12px", border: `1.5px solid ${windowMode === "rolling" ? "#5b50d6" : "#e8e5f0"}`, borderRadius: 8, background: windowMode === "rolling" ? "#ede9fd" : "#fff" }}>
              <input type="radio" name="bwMode" checked={windowMode === "rolling"} onChange={() => setWindowMode("rolling")} style={{ accentColor: "#5b50d6", marginTop: 2 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: "#1a1520" }}>🔄 Klizni prozor</div>
                <div style={{ fontSize: 11, color: "#6b6880", marginBottom: 8 }}>Uvijek dostupno X dana unaprijed od danas</div>
                {windowMode === "rolling" && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <InputNumber
                      min={7}
                      max={730}
                      value={windowRollingDays}
                      onChange={(v) => setWindowRollingDays(v ?? 90)}
                      style={{ width: 90 }}
                    />
                    <span style={{ fontSize: 12, color: "#6b6880" }}>dana unaprijed</span>
                    <span style={{ fontSize: 11, color: "#5b50d6", fontWeight: 600 }}>
                      (= {Math.round(windowRollingDays / 30)} mj.)
                    </span>
                  </div>
                )}
              </div>
            </label>

            {/* Option: fixed date */}
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", padding: "10px 12px", border: `1.5px solid ${windowMode === "fixed_date" ? "#5b50d6" : "#e8e5f0"}`, borderRadius: 8, background: windowMode === "fixed_date" ? "#ede9fd" : "#fff" }}>
              <input type="radio" name="bwMode" checked={windowMode === "fixed_date"} onChange={() => setWindowMode("fixed_date")} style={{ accentColor: "#5b50d6", marginTop: 2 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: "#1a1520" }}>📅 Do određenog datuma</div>
                <div style={{ fontSize: 11, color: "#6b6880", marginBottom: 8 }}>Gosti ne mogu rezervisati nakon ovog datuma</div>
                {windowMode === "fixed_date" && (
                  <DatePicker
                    value={windowFixedDate}
                    onChange={(d) => setWindowFixedDate(d)}
                    format="DD.MM.YYYY"
                    placeholder="Odaberite krajnji datum"
                    disabledDate={(d) => d.isBefore(dayjs(), "day")}
                    style={{ width: 200 }}
                  />
                )}
              </div>
            </label>
          </div>
        </div>

        {/* ─── Pricing Rules ─────────────────────────────────────────── */}
        <Divider style={{ margin: "8px 0 16px" }}>
          <span style={{ fontSize: 13, color: "#6b6880", fontWeight: 500 }}>
            Posebne cijene &amp; popusti
          </span>
        </Divider>

        {pricingRules.length === 0 && (
          <div style={{ fontSize: 13, color: "#9893a8", marginBottom: 12, textAlign: "center" }}>
            Nema definisanih posebnih cijena. Koristit će se osnovna cijena za sve datume.
          </div>
        )}

        {pricingRules.map((rule, i) => (
          <div
            key={i}
            style={{
              background: "#f8f7ff",
              border: "1px solid #ede9fd",
              borderRadius: 10,
              padding: "12px 14px",
              marginBottom: 10,
            }}
          >
            <Row gutter={8} align="middle">
              <Col span={6}>
                <div style={{ fontSize: 11, color: "#6b6880", marginBottom: 4 }}>Od datuma</div>
                <DatePicker
                  value={rule.from}
                  onChange={(d) => updateRule(i, { from: d })}
                  style={{ width: "100%" }}
                  format="DD.MM.YYYY"
                  placeholder="Od"
                />
              </Col>
              <Col span={6}>
                <div style={{ fontSize: 11, color: "#6b6880", marginBottom: 4 }}>Do datuma</div>
                <DatePicker
                  value={rule.to}
                  onChange={(d) => updateRule(i, { to: d })}
                  style={{ width: "100%" }}
                  format="DD.MM.YYYY"
                  placeholder="Do"
                  disabledDate={(d) => rule.from ? d.isBefore(rule.from, "day") : false}
                />
              </Col>
              <Col span={5}>
                <div style={{ fontSize: 11, color: "#6b6880", marginBottom: 4 }}>Tip</div>
                <Select
                  value={rule.type}
                  onChange={(v) => updateRule(i, { type: v as PricingRuleType })}
                  style={{ width: "100%" }}
                >
                  <Option value="discount">Popust (%)</Option>
                  <Option value="custom">Custom cijena</Option>
                </Select>
              </Col>
              <Col span={5}>
                <div style={{ fontSize: 11, color: "#6b6880", marginBottom: 4 }}>
                  {rule.type === "discount" ? "Popust (%)" : "Cijena/noć"}
                </div>
                <InputNumber
                  value={rule.value}
                  onChange={(v) => updateRule(i, { value: v ?? 0 })}
                  min={0}
                  max={rule.type === "discount" ? 100 : undefined}
                  style={{ width: "100%" }}
                  formatter={(v) =>
                    rule.type === "discount"
                      ? `${v}%`
                      : `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
                  }
                  parser={(v) =>
                    rule.type === "discount"
                      ? Number(v?.replace("%", "")) as any
                      : Number(v?.replace(/\./g, "")) as any
                  }
                />
              </Col>
              <Col span={2} style={{ textAlign: "right", paddingTop: 20 }}>
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeRule(i)}
                  size="small"
                />
              </Col>
            </Row>
            <div style={{ marginTop: 8 }}>
              <Input
                value={rule.label}
                onChange={(e) => updateRule(i, { label: e.target.value })}
                placeholder="Opis (opciono, npr. Ljetna sezona, Vikend popust...)"
                size="small"
                style={{ fontSize: 12 }}
              />
            </div>
          </div>
        ))}

        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={() => setPricingRules((prev) => [...prev, emptyRule()])}
          style={{ width: "100%", borderColor: "#c4bfef", color: "#5b50d6" }}
        >
          Dodaj posebnu cijenu ili popust
        </Button>

        {/* ─── Weekday Rules ─────────────────────────────────────── */}
        {(() => {
          const DAYS = [
            { label: "Pon", value: 1 },
            { label: "Uto", value: 2 },
            { label: "Sri", value: 3 },
            { label: "Čet", value: 4 },
            { label: "Pet", value: 5 },
            { label: "Sub", value: 6 },
            { label: "Ned", value: 0 },
          ];

          const updateWRule = (i: number, patch: Partial<WeekdayRule>) =>
            setWeekdayRules((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

          const toggleDay = (i: number, day: number) => {
            const curr = weekdayRules[i].days;
            const next = curr.includes(day) ? curr.filter((d) => d !== day) : [...curr, day];
            updateWRule(i, { days: next });
          };

          return (
            <>
              <div style={{ marginTop: 12, marginBottom: 8, fontSize: 12, color: "#9893a8", display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ flex: 1, height: 1, background: "#e8e5f0" }} />
                <span style={{ fontWeight: 500, color: "#6b6880" }}>Cijene po danima u sedmici</span>
                <span style={{ flex: 1, height: 1, background: "#e8e5f0" }} />
              </div>
              <div style={{ fontSize: 11, color: "#9893a8", marginBottom: 10 }}>
                Primjenjuju se samo ako datum ne spada u posebnu cijenu iznad (datumska pravila imaju veći prioritet).
              </div>

              {weekdayRules.map((rule, i) => (
                <div key={i} style={{ background: "#f8f7ff", border: "1px solid #ede9fd", borderRadius: 10, padding: "12px 14px", marginBottom: 10 }}>
                  {/* Day toggles */}
                  <div style={{ display: "flex", gap: 5, marginBottom: 10 }}>
                    {DAYS.map((d) => (
                      <button
                        key={d.value}
                        type="button"
                        onClick={() => toggleDay(i, d.value)}
                        style={{
                          flex: 1,
                          padding: "5px 2px",
                          borderRadius: 6,
                          border: `1.5px solid ${rule.days.includes(d.value) ? "#5b50d6" : "#e8e5f0"}`,
                          background: rule.days.includes(d.value) ? "#5b50d6" : "#fff",
                          color: rule.days.includes(d.value) ? "#fff" : "#6b6880",
                          fontWeight: 600,
                          fontSize: 11,
                          cursor: "pointer",
                          transition: "all 0.15s",
                        }}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>

                  <Row gutter={8} align="middle">
                    <Col span={8}>
                      <div style={{ fontSize: 11, color: "#6b6880", marginBottom: 4 }}>Tip</div>
                      <Select
                        value={rule.type}
                        onChange={(v) => updateWRule(i, { type: v as PricingRuleType })}
                        style={{ width: "100%" }}
                        size="small"
                      >
                        <Option value="discount">Popust (%)</Option>
                        <Option value="custom">Custom cijena</Option>
                      </Select>
                    </Col>
                    <Col span={8}>
                      <div style={{ fontSize: 11, color: "#6b6880", marginBottom: 4 }}>
                        {rule.type === "discount" ? "Popust (%)" : "Cijena/noć"}
                      </div>
                      <InputNumber
                        value={rule.value}
                        onChange={(v) => updateWRule(i, { value: v ?? 0 })}
                        min={0}
                        max={rule.type === "discount" ? 100 : undefined}
                        size="small"
                        style={{ width: "100%" }}
                        formatter={(v) => rule.type === "discount" ? `${v}%` : `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                        parser={(v) => rule.type === "discount" ? Number(v?.replace("%", "")) as any : Number(v?.replace(/\./g, "")) as any}
                      />
                    </Col>
                    <Col span={6}>
                      <div style={{ fontSize: 11, color: "#6b6880", marginBottom: 4 }}>Opis (opciono)</div>
                      <Input
                        value={rule.label || ""}
                        onChange={(e) => updateWRule(i, { label: e.target.value })}
                        placeholder="npr. Vikend cijena"
                        size="small"
                      />
                    </Col>
                    <Col span={2} style={{ textAlign: "right", paddingTop: 20 }}>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => setWeekdayRules((prev) => prev.filter((_, idx) => idx !== i))}
                        size="small"
                      />
                    </Col>
                  </Row>
                </div>
              ))}

              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => setWeekdayRules((prev) => [...prev, { days: [], type: "discount", value: 10, label: "" }])}
                style={{ width: "100%", borderColor: "#c4bfef", color: "#5b50d6", marginBottom: 8 }}
              >
                Dodaj pravilo po danima
              </Button>
            </>
          );
        })()}

        {/* ─── Night Count Rules ─────────────────────────────────── */}
        <div style={{ marginTop: 12, marginBottom: 8, fontSize: 12, color: "#9893a8", display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ flex: 1, height: 1, background: "#e8e5f0" }} />
          <span style={{ fontWeight: 500, color: "#6b6880" }}>Cijena prema broju noći</span>
          <span style={{ flex: 1, height: 1, background: "#e8e5f0" }} />
        </div>
        <div style={{ fontSize: 11, color: "#9893a8", marginBottom: 10 }}>
          Posebna cijena za tačan broj noći (npr. 1 noć = 90€). Ako gost rezerviše više noći, primjenjuje se osnovna cijena.
        </div>

        {nightCountRules.map((rule, i) => (
          <div key={i} style={{ background: "#f8f7ff", border: "1px solid #ede9fd", borderRadius: 10, padding: "12px 14px", marginBottom: 10 }}>
            <Row gutter={8} align="middle">
              <Col span={6}>
                <div style={{ fontSize: 11, color: "#6b6880", marginBottom: 4 }}>Broj noći</div>
                <InputNumber
                  value={rule.nights}
                  onChange={(v) => setNightCountRules((prev) => prev.map((r, idx) => idx === i ? { ...r, nights: v ?? 1 } : r))}
                  min={1}
                  max={365}
                  style={{ width: "100%" }}
                  size="small"
                  addonAfter="noći"
                />
              </Col>
              <Col span={10}>
                <div style={{ fontSize: 11, color: "#6b6880", marginBottom: 4 }}>Cijena po noći</div>
                <InputNumber
                  value={rule.pricePerNight}
                  onChange={(v) => setNightCountRules((prev) => prev.map((r, idx) => idx === i ? { ...r, pricePerNight: v ?? 0 } : r))}
                  min={0}
                  style={{ width: "100%" }}
                  size="small"
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                  parser={(v) => Number(v?.replace(/\./g, "")) as any}
                />
              </Col>
              <Col span={6}>
                <div style={{ fontSize: 11, color: "#6b6880", marginBottom: 4 }}>Opis (opciono)</div>
                <Input
                  value={rule.label || ""}
                  onChange={(e) => setNightCountRules((prev) => prev.map((r, idx) => idx === i ? { ...r, label: e.target.value || undefined } : r))}
                  placeholder="npr. Jedna noć"
                  size="small"
                />
              </Col>
              <Col span={2} style={{ textAlign: "right", paddingTop: 20 }}>
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => setNightCountRules((prev) => prev.filter((_, idx) => idx !== i))}
                  size="small"
                />
              </Col>
            </Row>
          </div>
        ))}

        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={() => setNightCountRules((prev) => [...prev, { nights: 1, pricePerNight: 0 }])}
          style={{ width: "100%", borderColor: "#c4bfef", color: "#5b50d6", marginBottom: 8 }}
        >
          Dodaj cijenu po broju noći
        </Button>

        {/* ─── Extras / Dodatna ponuda ──────────────────────────── */}
        <Divider style={{ margin: "16px 0" }}>
          <span style={{ fontSize: 13, color: "#6b6880", fontWeight: 500 }}>
            Dodatna ponuda uz rezervaciju
          </span>
        </Divider>

        <div style={{ fontSize: 12, color: "#9893a8", marginBottom: 12 }}>
          Usluge i proizvodi koje nudite uz boravak (kvadovi, hrana, pića...). Gosti ih mogu dodati pri rezervaciji.
        </div>

        {aptExtras.map((extra, i) => (
          <div
            key={i}
            style={{
              background: "#f8f7ff",
              border: "1px solid #ede9fd",
              borderRadius: 10,
              padding: "12px 14px",
              marginBottom: 10,
            }}
          >
            <Row gutter={8} align="middle">
              <Col span={9}>
                <div style={{ fontSize: 11, color: "#6b6880", marginBottom: 4 }}>Naziv</div>
                <Input
                  value={extra.name}
                  onChange={(e) => updateExtra(i, { name: e.target.value })}
                  placeholder="npr. Quad / Džip tura"
                  size="small"
                />
              </Col>
              <Col span={6}>
                <div style={{ fontSize: 11, color: "#6b6880", marginBottom: 4 }}>Redovna cijena</div>
                <InputNumber
                  value={extra.regularPrice}
                  onChange={(v) => updateExtra(i, { regularPrice: v ?? 0 })}
                  min={0}
                  style={{ width: "100%" }}
                  size="small"
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                  parser={(v) => Number(v?.replace(/\./g, "")) as any}
                />
              </Col>
              <Col span={6}>
                <div style={{ fontSize: 11, color: "#6b6880", marginBottom: 4 }}>Cijena s popustom</div>
                <InputNumber
                  value={extra.discountedPrice ?? undefined}
                  onChange={(v) => updateExtra(i, { discountedPrice: v ?? null })}
                  min={0}
                  style={{ width: "100%" }}
                  size="small"
                  placeholder="opciono"
                  formatter={(v) => v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".") : ""}
                  parser={(v) => Number(v?.replace(/\./g, "")) as any}
                />
              </Col>
              <Col span={3} style={{ textAlign: "right", paddingTop: 20 }}>
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeExtra(i)}
                  size="small"
                />
              </Col>
            </Row>
            <div style={{ marginTop: 8 }}>
              <Select
                value={extra.unit || "per_unit"}
                onChange={(value) => updateExtra(i, { unit: value })}
                size="small"
                style={{ width: "100%", fontSize: 12 }}
              >
                {PRICING_UNITS.map((u) => (
                  <Option key={u} value={u}>{PRICING_UNIT_LABELS[u]}</Option>
                ))}
              </Select>
            </div>
          </div>
        ))}

        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={() => setAptExtras((prev) => [...prev, { name: "", regularPrice: 0, discountedPrice: null, unit: "per_unit" }])}
          style={{ width: "100%", borderColor: "#c4bfef", color: "#5b50d6" }}
        >
          Dodaj stavku ponude
        </Button>

        {/* ─── Last Minute & Early Booking ──────────────────────── */}
        <Divider style={{ margin: "16px 0" }}>
          <span style={{ fontSize: 13, color: "#6b6880", fontWeight: 500 }}>
            Last Minute &amp; Early Booking
          </span>
        </Divider>

        <div style={{ fontSize: 12, color: "#9893a8", marginBottom: 12 }}>
          Automatski popusti koji se prikazuju kao banneri na javnoj stranici apartmana.
        </div>

        {/* Last Minute */}
        <div style={{ background: lastMinuteRule.enabled ? "linear-gradient(135deg, #fff1f1, #fff5f0)" : "#f8f7ff", border: `1.5px solid ${lastMinuteRule.enabled ? "#fca5a5" : "#ede9fd"}`, borderRadius: 10, padding: "14px 16px", marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: lastMinuteRule.enabled ? 14 : 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 20 }}>⚡</span>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, color: "#1a1520" }}>Last Minute</div>
                <div style={{ fontSize: 11, color: "#6b6880" }}>Popust za rezervacije u zadnji tren</div>
              </div>
            </div>
            <Switch
              checked={lastMinuteRule.enabled}
              onChange={(v) => setLastMinuteRule((r) => ({ ...r, enabled: v }))}
              style={{ background: lastMinuteRule.enabled ? "#dc2626" : undefined }}
            />
          </div>
          {lastMinuteRule.enabled && (
            <Row gutter={10}>
              <Col span={8}>
                <div style={{ fontSize: 11, color: "#6b6880", marginBottom: 4 }}>Rezerviši unutar</div>
                <InputNumber
                  value={lastMinuteRule.daysThreshold}
                  onChange={(v) => setLastMinuteRule((r) => ({ ...r, daysThreshold: v ?? 3 }))}
                  min={1}
                  max={30}
                  addonAfter="dana"
                  style={{ width: "100%" }}
                  size="small"
                />
              </Col>
              <Col span={8}>
                <div style={{ fontSize: 11, color: "#6b6880", marginBottom: 4 }}>Popust</div>
                <InputNumber
                  value={lastMinuteRule.discount}
                  onChange={(v) => setLastMinuteRule((r) => ({ ...r, discount: v ?? 10 }))}
                  min={1}
                  max={90}
                  formatter={(v) => `${v}%`}
                  parser={(v) => Number(v?.replace("%", "")) as any}
                  style={{ width: "100%" }}
                  size="small"
                />
              </Col>
              <Col span={8}>
                <div style={{ fontSize: 11, color: "#6b6880", marginBottom: 4 }}>Opis (opciono)</div>
                <Input
                  value={lastMinuteRule.label || ""}
                  onChange={(e) => setLastMinuteRule((r) => ({ ...r, label: e.target.value || undefined }))}
                  placeholder="npr. Posebna ponuda!"
                  size="small"
                />
              </Col>
            </Row>
          )}
        </div>

        {/* Early Booking */}
        <div style={{ background: earlyBookingRule.enabled ? "linear-gradient(135deg, #f0f9ff, #eff6ff)" : "#f8f7ff", border: `1.5px solid ${earlyBookingRule.enabled ? "#7dd3fc" : "#ede9fd"}`, borderRadius: 10, padding: "14px 16px", marginBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: earlyBookingRule.enabled ? 14 : 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 20 }}>🗓</span>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, color: "#1a1520" }}>Early Booking</div>
                <div style={{ fontSize: 11, color: "#6b6880" }}>Popust za rane rezervacije</div>
              </div>
            </div>
            <Switch
              checked={earlyBookingRule.enabled}
              onChange={(v) => setEarlyBookingRule((r) => ({ ...r, enabled: v }))}
              style={{ background: earlyBookingRule.enabled ? "#0284c7" : undefined }}
            />
          </div>
          {earlyBookingRule.enabled && (
            <Row gutter={10}>
              <Col span={8}>
                <div style={{ fontSize: 11, color: "#6b6880", marginBottom: 4 }}>Rezerviši barem</div>
                <InputNumber
                  value={earlyBookingRule.daysThreshold}
                  onChange={(v) => setEarlyBookingRule((r) => ({ ...r, daysThreshold: v ?? 30 }))}
                  min={7}
                  max={365}
                  addonAfter="dana"
                  style={{ width: "100%" }}
                  size="small"
                />
              </Col>
              <Col span={8}>
                <div style={{ fontSize: 11, color: "#6b6880", marginBottom: 4 }}>Popust</div>
                <InputNumber
                  value={earlyBookingRule.discount}
                  onChange={(v) => setEarlyBookingRule((r) => ({ ...r, discount: v ?? 10 }))}
                  min={1}
                  max={90}
                  formatter={(v) => `${v}%`}
                  parser={(v) => Number(v?.replace("%", "")) as any}
                  style={{ width: "100%" }}
                  size="small"
                />
              </Col>
              <Col span={8}>
                <div style={{ fontSize: 11, color: "#6b6880", marginBottom: 4 }}>Opis (opciono)</div>
                <Input
                  value={earlyBookingRule.label || ""}
                  onChange={(e) => setEarlyBookingRule((r) => ({ ...r, label: e.target.value || undefined }))}
                  placeholder="npr. Planirajte unaprijed!"
                  size="small"
                />
              </Col>
            </Row>
          )}
        </div>
      </Form>
    </Modal>
  );
};

export default AddApartmentModal;
